import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import Typography from '@mui/material/Typography';
import {
    Form,
    SelectInput,
    useCreate,
    useDataProvider,
    useDelete,
    useListContext,
    useNotify,
    useTranslate,
    useUnselectAll,
} from 'react-admin';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import {
    ANNULEE_STATE_KEY,
    AVATAR_PROPERTIES,
    AVATAR_TRACEABILITY_NOTES,
    AVATARS,
    CHANGE_DEMANDS_TO,
    CHANGE_NODE_BY_HUMAN_ID,
    EN_ATTENTE_REALISATION_STATE_KEY,
    INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT,
    INTERVENTION_CREATED_FROM_AVATAR_PROPERTY,
    INTERVENTION_TYPE_NAME_VALUES,
    PLANNED_DATE_AVATAR_PROPERTY,
    TRACEABILITY_TRACKING_V2_CHANGE_NODE_1,
} from '@/utils/CONST';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import { nth } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { getAvatarPropertyId, getAvatarPropertyValue } from '@/utils/getProperty';
import { Avatar } from '@/utils/types';
import { useUpdate } from 'react-admin';

interface Props {
    onClose: any;
    open: boolean;
    choices: {
        id: string;
        name: string;
    }[];
    /** Si true, usa la API avatars/change_node_by_human_id (meta api v2) como el cambio de estado de un solo item */
    useAvatarsChangeNodeApi?: boolean;
    /** Misma storeKey que la List que contiene la selección (ej. 'disordersTreatmentList') para deseleccionar correctamente */
    storeKey?: string;
    /** Llamado cuando el cambio de estado termina con éxito (p. ej. para cerrar modo multi-selección) */
    onSuccess?: () => void;
    /** Llamado al enviar el formulario para mostrar loading (p. ej. backdrop) */
    onLoadingStart?: () => void;
    /** Llamado cuando terminan todas las operaciones (éxito o error) para ocultar loading */
    onLoadingEnd?: () => void;
}

const ChangeStatusDialog = ({
    onClose,
    open,
    choices,
    useAvatarsChangeNodeApi = false,
    storeKey,
    onSuccess,
    onLoadingStart,
    onLoadingEnd,
}: Props) => {
    const translate = useTranslate();
    const { selectedIds } = useListContext();
    const unselectAll = useUnselectAll(AVATARS, storeKey);
    const [create, { isLoading }] = useCreate();
    const [update] = useUpdate();
    const [deleteOne] = useDelete();
    const queryClient = useQueryClient();
    const notify = useNotify();
    const { refetch } = useListContext();

    const dataProvider = useDataProvider();

    const handleSubmit = ({ idForNodeHumanReadable }: any) => {
        onLoadingStart?.();
        const tmpSelectedIds = [...selectedIds];
        unselectAll();
        onClose();

        const promises = tmpSelectedIds.map((avatarUriId: any) => {
            if (useAvatarsChangeNodeApi) {
                return create(
                    CHANGE_NODE_BY_HUMAN_ID,
                    {
                        data: {
                            avatar: avatarUriId,
                            humanId: idForNodeHumanReadable,
                            extraData: [],
                        },
                        meta: { api: 'v2' },
                    },
                    { returnPromise: true },
                ).then(() => {
                    queryClient.refetchQueries({ queryKey: [AVATAR_TRACEABILITY_NOTES] });
                    refetch();
                    notify('disorder.actions.change_status.one', { type: 'success' });
                });
            }

            return create(
                TRACEABILITY_TRACKING_V2_CHANGE_NODE_1,
                {
                    data: {
                        avatar: avatarUriId,
                        idForNodeHumanReadable,
                        version: 1,
                        extraData: [],
                    },
                },
                {
                    returnPromise: true,
                },
            ).then(async () => {
                if (
                    idForNodeHumanReadable ===
                    INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT[EN_ATTENTE_REALISATION_STATE_KEY].id
                ) {
                    const value = await dataProvider.getList(AVATAR_PROPERTIES, {
                        filter: { 'avatar.id': avatarUriId },
                        pagination: { page: 1, perPage: 100 },
                        sort: { field: 'id', order: 'ASC' },
                    });
                    const properties = value.data;

                    const planned_date = getAvatarPropertyId(properties, PLANNED_DATE_AVATAR_PROPERTY);

                    deleteOne(
                        AVATAR_PROPERTIES,
                        {
                            id: planned_date,
                        },
                        {
                            onSuccess: () => {
                                queryClient.refetchQueries({ queryKey: [AVATAR_TRACEABILITY_NOTES] });
                                // if (callback) callback(idForNodeHumanReadable, createdFrom);
                                refetch();
                                notify('disorder.actions.change_status.one', { type: 'success' });
                            },
                        },
                    );
                } else if (
                    idForNodeHumanReadable === INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT[ANNULEE_STATE_KEY].id
                ) {
                    const propertiesValue = await dataProvider.getList(AVATAR_PROPERTIES, {
                        filter: { 'avatar.id': avatarUriId },
                        pagination: { page: 1, perPage: 100 },
                        sort: { field: 'id', order: 'ASC' },
                    });
                    const properties = propertiesValue.data;
                    const createdFrom = getAvatarPropertyValue(properties, INTERVENTION_CREATED_FROM_AVATAR_PROPERTY);

                    if (createdFrom === INTERVENTION_TYPE_NAME_VALUES[0]) {
                        const value = await dataProvider.getOne(AVATARS, {
                            id: avatarUriId,
                        });
                        const avatar: Avatar = value.data;

                        create(
                            CHANGE_DEMANDS_TO,
                            {
                                data: {
                                    groupAvatar: nth(avatar.groupAvatars),
                                    traceabilityHumanId: '0010',
                                    intervention: avatarUriId,
                                },
                            },
                            {
                                onSuccess: () => {
                                    update(
                                        AVATARS,
                                        { id: avatarUriId, data: { groupAvatars: [] } },
                                        { onSuccess: () => notify('disorder.actions.change_status.one', { type: 'success' }) },
                                    );
                                },
                            },
                        );
                    }
                } else {
                    queryClient.refetchQueries({ queryKey: [AVATAR_TRACEABILITY_NOTES] });
                    // if (callback) callback(idForNodeHumanReadable, createdFrom);
                    refetch();
                    notify('disorder.actions.change_status.one', { type: 'success' });
                }
            });
        });

        Promise.all(promises)
            .then(() => {
                refetch();
                notify('disorder.actions.change_status.many', { type: 'success' });
                onSuccess?.();
            })
            .finally(() => {
                onLoadingEnd?.();
            });
    };

    return (
        <DialogTemplate
            icon={<SyncAltIcon />}
            title={translate('buttons.change_status')}
            open={open}
            onClose={onClose}
            maxWidth={'xs'}
        >
            <Form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                        }}
                    >
                        <Typography>{translate('disorder.actions.change_status.label')}</Typography>

                        <FormInputsSeparator>
                            <SelectInput
                                source='idForNodeHumanReadable'
                                choices={choices}
                                helperText={false}
                                label={'disorder.actions.change_status.select_label'}
                            />
                        </FormInputsSeparator>
                    </Box>

                    <ButtonsBlock>
                        <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>

                        <LoadingButton variant={'contained'} type='submit' loading={isLoading}>
                            {translate('buttons.validate')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default ChangeStatusDialog;
